import pkceChallenge from "pkce-challenge";
import axios from 'axios';
import { REDIRECT_KEY } from '../../src/app/historyUtil';

export default class OAuth2PKCE {
  constructor() {
    this.idToken = '';
    this.systemId = '';
    this.scope = `openid ${TRIMBLE_ID_APP_NAME}`;
    this.clientId = TRIMBLE_ID_CLIENT_ID;
    this.redirectUri = `${window.location.origin}/`
    this.stateParam = this.saveState();
  }

  handleLogin() {  
    try {
      const challenge = pkceChallenge(64);

      const params = new URLSearchParams();
      params.append('scope', this.scope);
      params.append('client_id', this.clientId);
      params.append('redirect_uri', this.redirectUri);
      params.append('response_type', 'code');
      params.append('code_challenge_method', 'S256');
      params.append('code_challenge', challenge.code_challenge);

      if (this.stateParam) {
        params.append('state', this.stateParam);
      }

      const url = `${TRIMBLE_ID_URL}/oauth/authorize?${params.toString()}`;
      this.saveChallenge(challenge.code_challenge, challenge.code_verifier);

      window.location.href = url;
    } catch (error) {
      console.log('Error on handleLogin', error);
    }
  }

  async getAccessToken(authorizationCode) {
    return new Promise(async (resolve, reject) => {
      const codeVerifier = localStorage.getItem('code_verifier');
      if (codeVerifier && authorizationCode) {
        try {
          const challenge = pkceChallenge(64);
          const params = new URLSearchParams();
          params.append('client_id', TRIMBLE_ID_CLIENT_ID);
          params.append('code_verifier', codeVerifier);
          params.append('grant_type', 'authorization_code');
          params.append('redirect_uri', this.redirectUri);
          params.append('code', authorizationCode);
          params.append('code_challenge', challenge.code_challenge);
          const result = await this.fetchToken(params.toString());
          if (result && result.data) {
            result.data.code_verifier = challenge.code_verifier;
            result.data.authorization_code = authorizationCode;
            resolve(result.data);
          }
        } catch (error) {
          console.log('Error retriving accessToken', error);
          reject();
        }
      } else {
        reject();
      }
    });
  }

  async getAccessTokenWithRefreshToken() {
    if (localStorage.getItem('code_verifier')) {
      try {
        const challenge = pkceChallenge(64);
        const refreshToken = localStorage.getItem('refresh_token');
        const codeVerifier = localStorage.getItem('code_verifier');

        const params = new URLSearchParams();
        params.append('client_id', TRIMBLE_ID_CLIENT_ID);
        params.append('refresh_token', refreshToken);
        params.append('grant_type', 'refresh_token');
        params.append('code_verifier', codeVerifier);
        params.append('code_challenge', challenge.code_challenge);
        params.append('code_challenge_method', 'S256');

        const result = await this.fetchToken(params.toString());
        if (result && result.data) {
          result.data.code_verifier = challenge.code_verifier;
          this.saveTokens(result.data);
          return result.data.expires_in;
        } else {
          this.handleLogin();
        }
      } catch (error) {
        console.log('Error retriving acessToken with refreshToken', error);
        this.handleLogin();
      }
    } else {
      this.handleLogin();
    }
  }

  handleLogout() {
    try {
      const params = new URLSearchParams();
      params.append('id_token_hint', this.idToken);
      params.append('post_logout_redirect_uri', this.redirectUri);

      if (this.systemId && this.systemId !== 'peoplenet') {
        params.append('ref', this.systemId);
      }

      const url = `${TRIMBLE_ID_URL}/oauth/logout?${params.toString()}`;

      this.idToken = '';
      this.systemId = '';
      
      window.location.href = url;
    } catch (error) {
      console.log('Error on handleLogout', error);
    }
  }

  fetchToken(params) {
    const url = `${TRIMBLE_ID_URL}/oauth/token`;
    return axios.post(url, params, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded"
      }
    });
  }

  saveChallenge(codeChallenge, codeVerifier) {
    localStorage.setItem('code_challenge', codeChallenge);
    localStorage.setItem('code_verifier', codeVerifier);
  }

  saveTokens(tokens) {
    const { access_token, expires_in, id_token, refresh_token, code_verifier, authentication_code: authorization_code } = tokens;

    localStorage.setItem('access_token', access_token);
    localStorage.setItem('id_token', id_token);
    localStorage.setItem('expires_at', JSON.stringify((expires_in * 1000) + new Date().getTime()));
    localStorage.setItem('expires_in', expires_in);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('code_verifier', code_verifier);
    localStorage.setItem('authorization_code', authorization_code)
  }

  saveState() {
    const params = new URLSearchParams(window.location.search);
    if (!params.get('code')) {
      const searchParams = {};
      searchParams.state = {};
      searchParams.state[REDIRECT_KEY] = encodeURIComponent(window.location.href);
  
      if (params.get('ref')) {
        searchParams.state.ref = params.get('ref');
      }

      return new URLSearchParams(searchParams.state).toString();
    }
    
    return '';
  }

  hasAuthCode() {
    return localStorage.getItem('authorization_code') ? true : false;
  }

  hasAccessToken() {
    return localStorage.getItem('access_token') ? true : false;
  }

  clear() {
    this.idToken = localStorage.getItem('id_token');
    this.systemId = localStorage.getItem('systemId');

    window.localStorage.clear();
  }
}
